import { useState } from "react";
import { Timestamp } from "firebase/firestore";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { adapter } from "@/services/db";
import { useAuth } from "@/context/AuthContext";
import { logEdit } from "@/components/explorer/audit";
import { enumFor, labelOf } from "@/lib/fieldEnums";
import { toDate, fmtTs, fmtDate, inr } from "@/lib/format";
import { cn } from "@/lib/utils";

const pad = (n) => String(n).padStart(2, "0");
const toLocalDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const toLocalDT = (d) => `${toLocalDate(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}`;

// Write a JS Date back in the SAME shape the field already uses, so Brugo Web / Pro keep
// reading it: Firestore Timestamp, "YYYY-MM-DD", ISO string, or epoch seconds / ms.
export function dateLike(existing, d, kind) {
  if (!d) return null;
  if (existing instanceof Timestamp || existing?.toDate) return Timestamp.fromDate(d);
  if (typeof existing === "number") return existing < 1e11 ? Math.floor(d.getTime() / 1000) : d.getTime();
  if (typeof existing === "string") return /^\d{4}-\d{2}-\d{2}$/.test(existing) ? toLocalDate(d) : d.toISOString();
  return kind === "date" ? toLocalDate(d) : Timestamp.fromDate(d);
}
export const nowLike = (existing) => dateLike(existing ?? Timestamp.now(), new Date(), "datetime");

// Save several fields on one doc (audited per field). Returns true on success.
export async function saveFields(user, path, doc, patch, { touch = true } = {}) {
  const full = { ...patch };
  if (touch && !("updated_at" in patch)) full.updated_at = nowLike(doc.updated_at);
  try {
    await adapter.patchFields(path, doc.id, full);
    await Promise.all(Object.entries(patch).map(([k, v]) => logEdit(user, { path, id: doc.id, action: "update_field", field: k, before: doc[k], after: v, sensitive: false })));
    return true;
  } catch (e) {
    toast.error(e.code === "permission-denied" ? "Permission denied by Firestore rules" : e.message);
    return false;
  }
}

const show = (v, type) => {
  if (v == null || v === "") return <span className="text-muted-foreground">—</span>;
  if (type === "date") return fmtDate(v);
  if (type === "datetime") return fmtTs(v);
  if (type === "money") return inr(v);
  if (type === "bool") return v ? "Yes" : "No";
  if (type === "enum") return labelOf(v);
  if (typeof v === "object") return <span className="font-mono text-[11px]">{JSON.stringify(v).slice(0, 40)}</span>;
  return String(v);
};

// Click-to-edit cell for one Firestore field. type: text | textarea | number | money | date | datetime | bool | enum.
export function InlineEdit({ path, doc, field, type = "text", options, render, className = "", placeholder, testId }) {
  const { user } = useAuth();
  const v = doc[field];
  const opts = type === "enum" ? Array.from(new Set([...(v ? [v] : []), ...(options || enumFor(path, field) || [])])) : null;
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const tid = testId || `ie-${field}-${doc.id}`;

  const start = () => {
    const d = toDate(v);
    if (type === "date") setDraft(d ? toLocalDate(d) : "");
    else if (type === "datetime") setDraft(d ? toLocalDT(d) : "");
    else if (type === "bool") setDraft(!!v);
    else setDraft(v == null ? "" : String(v));
  };
  const save = async (val = draft) => {
    let next;
    if (type === "number" || type === "money") { if (val === "") next = null; else { next = Number(val); if (Number.isNaN(next)) return toast.error("Not a number"); } }
    else if (type === "date" || type === "datetime") next = val ? dateLike(v, new Date(type === "date" ? `${val}T00:00:00` : val), type) : null;
    else next = val;
    if (next === v) { setOpen(false); return; }
    setBusy(true);
    const ok = await saveFields(user, path, doc, { [field]: next });
    setBusy(false);
    if (ok) { toast.success(`${field} updated`); setOpen(false); }
  };

  // Enum: a compact select is the editor itself (one click to change).
  if (type === "enum") {
    return (
      <span onClick={(e) => e.stopPropagation()} className={className}>
        <Select value={v || ""} onValueChange={(nv) => save(nv)} disabled={busy}>
          <SelectTrigger className="h-7 min-w-[132px] text-[11px] bg-background" data-testid={tid}><SelectValue placeholder={placeholder || field} /></SelectTrigger>
          <SelectContent className="z-[120]">{opts.map((o) => <SelectItem key={o} value={o} className="text-xs">{labelOf(o)}</SelectItem>)}</SelectContent>
        </Select>
      </span>
    );
  }

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) start(); }}>
      <PopoverTrigger asChild>
        <button type="button" onClick={(e) => e.stopPropagation()} data-testid={tid}
          className={cn("group/ie text-left rounded px-1 -mx-1 hover:bg-primary/5 hover:ring-1 hover:ring-primary/30 transition-colors inline-flex items-center gap-1 max-w-full", className)}>
          <span className={["money", "number", "date", "datetime", "bool"].includes(type) ? "whitespace-nowrap" : "truncate"}>{render ? render(v) : show(v, type)}</span>
          <Icons.Pencil className="h-3 w-3 shrink-0 text-muted-foreground opacity-0 group-hover/ie:opacity-100" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3 z-[120]" align="start" onClick={(e) => e.stopPropagation()}>
        <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-2">{field}</p>
        {type === "bool" ? (
          <div className="flex items-center gap-2"><Switch checked={!!draft} onCheckedChange={setDraft} data-testid={`${tid}-input`} /><span className="text-sm">{draft ? "Yes" : "No"}</span></div>
        ) : type === "textarea" ? (
          <Textarea autoFocus rows={4} value={draft} onChange={(e) => setDraft(e.target.value)} data-testid={`${tid}-input`} />
        ) : (
          <Input autoFocus type={type === "number" || type === "money" ? "number" : type === "date" ? "date" : type === "datetime" ? "datetime-local" : "text"} step="any"
            value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()} className="h-8" data-testid={`${tid}-input`} />
        )}
        <div className="flex gap-2 mt-3">
          <Button size="sm" className="h-7" onClick={() => save()} disabled={busy} data-testid={`${tid}-save`}>{busy ? "Saving…" : "Save"}</Button>
          {(type === "date" || type === "datetime") && <Button size="sm" variant="ghost" className="h-7" onClick={() => { const d = new Date(); setDraft(type === "date" ? toLocalDate(d) : toLocalDT(d)); }}>Now</Button>}
          <Button size="sm" variant="ghost" className="h-7 ml-auto" onClick={() => setOpen(false)}>Cancel</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
